import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconButton, Stack, Tooltip } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { changePassword } from 'redux/slices/auth/authSlice';
import { AppDispatch, RootState } from 'redux/store';
import { logoutUser } from 'redux/slices/user/userSlice';
import { FloatingField } from 'components';
import { notify } from 'components/Common/Toast';
import AuthLayaout from 'pages/Auth/AuthLayout';
import Routes from 'constants/routes';
import CustomButton from 'components/Common/Button';
import CardWrapper from 'components/Common/CardWrapper';
import MuiLoader from 'components/Common/Loader';
import DrawerLeft from 'components/Common/Drawer';

const formSchema = yup
    .object({
        currentPassword: yup.string().required('Enter your current password'),
        newPassword: yup
            .string()
            .required('Enter your new password')
            .min(6, 'Password is too short - should be 6 chars minimum.')
            .max(16, 'Password is too long - should be 16 chars maximun.')
            .matches(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{6,}$/, 'Password should have at least 1 uppercase, 1 lowercase and 1 digit.'),
        confirmPassword: yup
            .string()
            .required('Confirm password')
            .min(6, 'Password is too short - should be 6 chars minimum.')
            .max(16, 'Password is too long - should be 16 chars maximun.')
            .matches(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{6,}$/, 'Password should have at least 1 uppercase, 1 lowercase and 1 digit.')
            .oneOf([yup.ref('newPassword')], 'Your passwords do not match.'),
    })
    .required();

export interface ChangePasswordFormInputs {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

export default function MyAccountChangePassword() {
    const navigation = useNavigate();
    const location = useLocation();
    const { type } = location?.state || {};
    const dispatch = useDispatch<AppDispatch>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const logoutLoading = useSelector((state: RootState) => state.user?.logoutLoading);

    let description = `Your new password must be different from previous used passwords and must be of at least 6 characters.`;
    if (type !== 'resetPassword') {
        description = `Please create your permanent password that you will use to login to this account.`;
    }

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<ChangePasswordFormInputs>({
        resolver: yupResolver(formSchema),
        defaultValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        },
    });
    const submit = async (data: { [x: string]: any }) => {
        const { currentPassword, newPassword } = data;
        setIsLoading(true);
        try {
            const result: any = await dispatch(changePassword({ currentPassword, newPassword }));
            const { contextResponse } = result?.payload || {};
            if (contextResponse?.statusCode) {
                notify('Password Changed Successfully!', 'success');

                if (type !== 'resetPassword') {
                    dispatch(handleLogout());
                } else {
                    navigation(Routes.account.main);
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };
    const handleLogout = async () => {
        await dispatch(logoutUser());
        navigation(Routes.auth.login);
    };
    const content = () => {
        return (
            <div className="Auth-form">
                {type !== 'resetPassword' && (
                    <Tooltip title="Logout">
                        <IconButton disabled={logoutLoading} onClick={handleLogout} sx={{ position: 'absolute', top: '5px', right: '5px' }}>
                            {logoutLoading ? <MuiLoader size={20} loading={logoutLoading} /> : <LogoutIcon />}
                        </IconButton>
                    </Tooltip>
                )}
                <p className="Auth-form-title">Create new password</p>
                <p className="Auth-form-subtitle">{description}</p>
                <Controller
                    name="currentPassword"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                        <FloatingField
                            label="Current Password"
                            value={value}
                            isPassword={true}
                            onChange={onChange}
                            error={errors.currentPassword?.message}
                        />
                    )}
                />
                <Controller
                    name="newPassword"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                        <FloatingField
                            label="Password"
                            value={value}
                            isPassword={true}
                            onChange={onChange}
                            fieldDescription={'Must be 6-16 characters long.'}
                            error={errors.newPassword?.message}
                        />
                    )}
                />
                <Controller
                    name="confirmPassword"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                        <FloatingField
                            label="Confirm password"
                            value={value}
                            isPassword={true}
                            onChange={onChange}
                            fieldDescription={'Both passwords must match.'}
                            error={errors.confirmPassword?.message}
                        />
                    )}
                />

                <CustomButton
                    loading={isLoading}
                    title={type === 'resetPassword' ? 'UPDATE PASSWORD' : 'Save'}
                    onClick={handleSubmit(submit)}
                    sx={{ fontSize: { xs: 14, sm: 18 }, mt: 2 }}
                />
            </div>
        );
    };
    if (type === 'resetPassword') {
        return (
            <DrawerLeft>
                <CardWrapper stackProps={{ alignItems: 'center' }}>
                    <Stack width={{ lg: '50%' }} justifyContent="center">
                        {content()}
                    </Stack>
                </CardWrapper>
            </DrawerLeft>
        );
    }
    return <AuthLayaout>{content()}</AuthLayaout>;
}
